export function renderTermsPage() {
  return `
    <main id="terms" class="legal-page section narrow-copy">
      <a class="back-link" href="/" data-route>Back to home</a>
      <div class="contact-intro">
        <p class="eyebrow">Legal</p>
        <h1>Terms of Use</h1>
      </div>
      <div class="terms-content">
        <p><strong>Effective:</strong> 2026<br>
        <strong>Last updated:</strong> 22 April 2026</p>

        <p>Welcome to womyn.network. These Terms of Use govern your access to and use of the womyn.network website, platform, features, and related content (the "Service"). By accessing, browsing, creating an account, or using the Service, you agree to be bound by these Terms. If you do not agree, you must not use womyn.network.</p>

        <h3>1. About womyn.network</h3>
        <p>womyn.network is a women-only digital platform designed around privacy, safety, trust, and more relaxed social connection in a women-centred online space.</p>
        <p>The Service may include community spaces, social posting features, profiles, moderation tools, chat features, privacy-protective design features, experimental safety technologies, and prototype or beta functionality.</p>

        <h3>2. Eligibility</h3>
        <p>To use the Service, you must:</p>
        <ul>
          <li>be at least 18 years old; and</li>
          <li>meet the platform's women-only eligibility rule set out below.</li>
        </ul>
        <p>womyn.network is intended exclusively for adult women. By creating an account or using the Service, you confirm that you meet this requirement and that the information you provide to us is accurate.</p>
        <p>This women-only rule is a core condition of access to the Service. It is part of the platform's purpose, design, moderation model, and safeguarding approach.</p>
        <p>We reserve the right to refuse access, suspend accounts, request additional information, or remove users where we reasonably believe this eligibility requirement is not met or has been circumvented.</p>

        <h3>3. Purpose of the women-only rule</h3>
        <p>womyn.network was created to provide a dedicated women-centred space shaped by women's privacy, safety, dignity, and ability to interact more freely online.</p>
        <p>We maintain this boundary because we consider it central to the platform's purpose and to the integrity of the environment we are building.</p>

        <h3>4. Beta status</h3>
        <p>womyn.network may be offered in beta or prototype form.</p>
        <p>This means that parts of the Service may be incomplete, experimental, changed, interrupted, or removed at any time. Features may not work as expected. We do not guarantee uninterrupted availability, permanent access, compatibility with all devices, or that any part of the Service will remain available in its current form.</p>
        <p>By using a beta product, you understand that you may encounter bugs, errors, outages, design changes, and unfinished functionality.</p>

        <h3>5. Your account</h3>
        <p>You are responsible for your account and for keeping your login details secure.</p>
        <p>You agree not to:</p>
        <ul>
          <li>create an account using false or misleading information;</li>
          <li>impersonate another person;</li>
          <li>share your account with anyone else;</li>
          <li>attempt to access another user's account; or</li>
          <li>use the Service in a way that undermines the safety, integrity, or intended membership boundary of the platform.</li>
        </ul>
        <p>You can and must notify us promptly at hello@womyn.network if you believe your account has been compromised.</p>

        <h3>6. Acceptable use & Chat</h3>
        <p>You agree to use womyn.network respectfully and safely.</p>
        <p>You must not use the Service to:</p>
        <ul>
          <li>harass, threaten, stalk, intimidate, or abuse others;</li>
          <li>upload, post, send, or share harmful, defamatory, obscene, sexually exploitative, or hateful material;</li>
          <li>share another person's private, confidential, or intimate information without permission;</li>
          <li>post non-consensual intimate images or material intended to humiliate, exploit, or endanger another person;</li>
          <li>impersonate another person or misrepresent your identity;</li>
          <li>misuse the chat features to send unsolicited mass messages, spam, or share private conversations without consent;</li>
          <li>collect data from other users without permission;</li>
          <li>spam, scam, phish, or distribute malware;</li>
          <li>test, probe, reverse engineer, interfere with, or disrupt the Service or its security;</li>
          <li>bypass moderation, safety systems, account restrictions, or access controls;</li>
          <li>use the platform for commercial scraping, mass messaging, or unauthorised advertising; or</li>
          <li>upload content that infringes copyright, trade marks, privacy, or other rights.</li>
        </ul>
        <p>We may remove content or restrict access where we reasonably believe use of the Service breaches these Terms or creates risk for users or the platform.</p>

        <h3>7. Community safety and moderation</h3>
        <p>womyn.network is a moderated platform. We review accounts, posts, messages, reports, or other activity where reasonably necessary to:</p>
        <ul>
          <li>enforce these Terms;</li>
          <li>maintain the women-only rule;</li>
          <li>investigate misuse, safety incidents, or fraud; and</li>
          <li>protect users, staff, or the Service.</li>
        </ul>
        <p>Moderation may include warnings, content removal, temporary restrictions, suspension, or permanent account termination.</p>
        <p>We are not obliged to host, publish, restore, or maintain any particular content, account, username, feature, or community space at this stage.</p>

        <h3>8. User content</h3>
        <p>You retain ownership of the content you create and upload to womyn.network, subject to any rights in third-party material included in that content.</p>
        <p>You are responsible for the content you post and confirm that you have the rights needed to post it.</p>

        <h3>9. Privacy-protective and AI-related features</h3>
        <p>womyn.network may include privacy or safety technologies designed to reduce misuse of shared images or content, including experimental browser-side AI protective processing.</p>
        <p>Where such features are offered, they are intended to support user privacy and safety, but they are not guaranteed to prevent copying, manipulation, extraction, scraping, or malicious reuse.</p>
        <p>You remain responsible for deciding what you choose to upload or share.</p>

        <h3>10. Reporting content or users</h3>
        <p>If you believe content, chat messages, or behaviour on the Service breaches these Terms or creates a safety concern, you can report it by contacting hello@womyn.network.</p>
        <p>We may investigate reports but do not guarantee any particular outcome, timeframe, or response.</p>

        <h3>11. Suspension and termination</h3>
        <p>We may suspend, restrict, or terminate your access to all or part of the Service at any time where we reasonably believe that:</p>
        <ul>
          <li>you have breached these Terms;</li>
          <li>you do not meet the eligibility requirements;</li>
          <li>your use creates security or safety risks;</li>
          <li>doing so is necessary to protect the community, the platform, or third parties; or</li>
          <li>we are changing, pausing, or discontinuing the Service.</li>
        </ul>
        <p>You may stop using the Service at any time.</p>

        <h3>12. Intellectual property</h3>
        <p>All rights in womyn.network, other than user content, belong to us or our licensors. This includes the platform name, branding, software, design, text, graphics, interfaces, databases, and site content.</p>
        <p>You may not copy, modify, distribute, sell, or exploit any part of the Service except with our written permission.</p>

        <h3>13. Third-party services and links</h3>
        <p>The Service may contain links to third-party websites, tools, libraries, or services. We are not responsible for third-party content, availability, policies, or practices.</p>

        <h3>14. Service availability</h3>
        <p>We do not guarantee that womyn.network will always be available, error-free, or compatible with your device or browser.</p>
        <p>Access may be suspended or limited for maintenance, updates, security reasons, moderation, or technical issues.</p>

        <h3>15. Changes to these Terms</h3>
        <p>We may update these Terms from time to time.</p>
        <p>Where changes are material, we will take reasonable steps to notify users, such as by updating the date above, posting a notice on the Service, or contacting users through the platform or by email.</p>
        <p>Your continued use of the Service after updated Terms take effect means you accept the revised Terms.</p>

        <h3>Contact</h3>
        <p>If you have questions about these Terms, please contact:</p>
        <p><strong>Email:</strong> hello@womyn.network</p>

        <p><em>This page is intended as platform policy and product copy. Before launch, the final wording will be reviewed by a qualified professional.</em></p>
      </div>
    </main>
  `;
}
